import React from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

const TrendChart = ({ data, selectedMetric }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const dataPoint = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg shadow-lg p-3 md:p-4">
          <p className="text-xs md:text-sm font-medium text-foreground mb-2">{dataPoint?.month || label}</p>
          <div className="space-y-1">
            <p className="text-xs md:text-sm text-muted-foreground">
              Percent: <span className="font-semibold text-primary">{dataPoint?.percent?.toFixed(4)}%</span>
            </p>
            <p className="text-xs md:text-sm text-muted-foreground">
              Stablecoins: <span className="font-semibold text-foreground">${(dataPoint?.stablecoins_usd / 1e9)?.toFixed(2)}B</span>
            </p>
            <p className="text-xs md:text-sm text-muted-foreground">
              M2: <span className="font-semibold text-foreground">${(dataPoint?.m2_usd / 1e12)?.toFixed(2)}T</span>
            </p>
            {dataPoint?.change !== undefined && dataPoint?.change !== 0 && (
              <p className={`text-xs md:text-sm ${dataPoint?.change >= 0 ? 'text-success' : 'text-error'}`}>
                Change: {dataPoint?.change >= 0 ? '+' : ''}{dataPoint?.change?.toFixed(2)}%
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // Format X-axis to show labels every 3-6 months for readability
  const formatXAxis = (value, index) => {
    // Show every 3rd label for monthly data to avoid crowding
    if (data?.length > 24) {
      // For longer periods (>2 years), show every 6 months
      return index % 6 === 0 ? value : '';
    } else if (data?.length > 12) {
      // For 1-2 years, show every 3 months
      return index % 3 === 0 ? value : '';
    }
    // For shorter periods, show every month
    return value;
  };

  return (
    <div className="w-full h-64 md:h-80 lg:h-96" aria-label="Historical Trend Line Chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorRatio" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis 
            dataKey="date" 
            stroke="var(--color-muted-foreground)"
            tick={{ fontSize: 12 }}
            tickMargin={8}
            tickFormatter={formatXAxis}
            interval="preserveStartEnd"
          />
          <YAxis 
            stroke="var(--color-muted-foreground)"
            tick={{ fontSize: 12 }}
            tickMargin={8}
            tickFormatter={(value) => `${value?.toFixed(2)}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="percent"
            stroke="var(--color-primary)"
            strokeWidth={2}
            fill="url(#colorRatio)"
            dot={false}
            activeDot={{ r: 5, fill: 'var(--color-primary)' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TrendChart;